import { useId, useState, type SyntheticEvent } from "react";
import dayjs from "dayjs";
import { useTranslation } from "react-i18next";
import { Check, Trash2 } from "lucide-react";
import { Button } from "~/atoms/button";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "~/atoms/dialog";
import { Input } from "~/atoms/input";
import { Label } from "~/atoms/label";
import { ToggleGroup, ToggleGroupItem } from "~/atoms/toggle-group";
import { cn } from "~/lib/utils";
import DatePicker from "~/molecules/DatePicker";
import {
  createAnnualWheelEvent,
  deleteAnnualWheelEvent,
  updateAnnualWheelEvent,
  useAnnualWheelEvents,
} from "./eventsStub";
import { eventColor } from "./EventSwatch";
import { formatDate } from "./helpers";
import { useText } from "./text";
import { annualWheelEventColors, type AnnualWheelEventData } from "./types";

// An event being added has no id yet
export interface EventDraft extends AnnualWheelEventData {
  id?: number;
}

interface Props {
  draft: EventDraft;
  onClose: () => void;
}

export const EventDialog = ({ draft, onClose }: Props) => {
  const { t } = useTranslation();
  const text = useText();
  const { mutate } = useAnnualWheelEvents();
  const { id } = draft;
  const [name, setName] = useState(draft.name);
  // dayjs reads a bare day as local time; Date would read it as UTC and can land a day early
  const [date, setDate] = useState<Date | undefined>(() =>
    dayjs(draft.date).toDate(),
  );
  const [color, setColor] = useState(draft.color);
  // Radix hands the focus back to its own trigger only, and this dialog has none
  const [opener] = useState(() => document.activeElement);
  const nameId = useId();
  const colorLabelId = useId();
  const trimmedName = name.trim();

  const closeAfter = async (request: Promise<void>) => {
    await request;
    await mutate();
    onClose();
  };

  const save = (event: SyntheticEvent) => {
    event.preventDefault();
    if (!date || !trimmedName) return;

    const data = {
      name: trimmedName,
      date: formatDate(date, "isoDate"),
      color,
    };

    void closeAfter(
      id === undefined
        ? createAnnualWheelEvent(data)
        : updateAnnualWheelEvent(id, data),
    );
  };

  return (
    <Dialog
      open
      onOpenChange={(isOpen) => {
        if (!isOpen) onClose();
      }}
    >
      <DialogContent
        className="sm:max-w-md"
        onCloseAutoFocus={() => {
          if (opener instanceof HTMLElement || opener instanceof SVGElement) {
            opener.focus();
          }
        }}
      >
        <form className="grid gap-4" onSubmit={save}>
          <DialogHeader>
            <DialogTitle>
              {id === undefined ? text.events.add : text.events.edit}
            </DialogTitle>
          </DialogHeader>

          <div className="grid gap-2">
            <Label htmlFor={nameId}>{text.events.name}</Label>
            <Input
              id={nameId}
              value={name}
              placeholder={text.events.namePlaceholder}
              onChange={(event) => setName(event.target.value)}
            />
          </div>

          <div className="grid gap-2">
            <Label>{text.events.date}</Label>
            <DatePicker value={date} onChange={setDate} />
          </div>

          <div className="grid gap-2">
            <Label id={colorLabelId}>{text.events.color}</Label>
            <ToggleGroup
              type="single"
              value={color}
              aria-labelledby={colorLabelId}
              // Pressing the chosen colour again reports an empty value
              onValueChange={(value) =>
                setColor(
                  annualWheelEventColors.find((option) => option === value) ??
                    color,
                )
              }
            >
              {annualWheelEventColors.map((option) => (
                <ToggleGroupItem
                  key={option}
                  value={option}
                  aria-label={text.eventColor[option]}
                >
                  <span
                    className={cn(
                      "flex size-5 items-center justify-center rounded-full bg-current",
                      eventColor[option],
                    )}
                  >
                    <Check className="text-background hidden size-3.5 group-data-[state=on]/toggle:block" />
                  </span>
                </ToggleGroupItem>
              ))}
            </ToggleGroup>
          </div>

          <DialogFooter
            className={cn(id !== undefined && "sm:justify-between")}
          >
            {id !== undefined && (
              <Button
                type="button"
                variant="ghost"
                className="text-destructive"
                onClick={() => void closeAfter(deleteAnnualWheelEvent(id))}
              >
                <Trash2 />
                {t(($) => $.commons.buttons.delete)}
              </Button>
            )}
            <div className="flex flex-col-reverse gap-2 sm:flex-row">
              <DialogClose asChild>
                <Button type="button" variant="outline">
                  {t(($) => $.commons.buttons.cancel)}
                </Button>
              </DialogClose>
              <Button type="submit" disabled={!date || !trimmedName}>
                {id === undefined
                  ? t(($) => $.commons.buttons.create)
                  : t(($) => $.commons.buttons.save)}
              </Button>
            </div>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};
